import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateAllAvailableAutomationTriggersQueries } from '../../queries/useAvailableAutomationTriggersQuery';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

interface MutationVariables {
	id: string;
	trigger: Api.IAutomationTrigger;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Gets the trigger path segment based on the automation trigger type.
 * Different trigger types use different API endpoints.
 */
function getTriggerPath(trigger: Api.IAutomationTrigger): string | null {
	switch (trigger?._type) {
		case Api.AutomationTriggerType.Tag:
			return 'Tag';
		case Api.AutomationTriggerType.NewLead:
			return 'NewLead';
		case Api.AutomationTriggerType.NewPolicyTrigger:
		case Api.AutomationTriggerType.ResourceSelector:
			return 'ResourceSelector';
		case Api.AutomationTriggerType.NewClient:
			return 'NewClient';
		case Api.AutomationTriggerType.NewDonor:
			return 'NewDonor';
		case Api.AutomationTriggerType.NewDonationTrigger:
			return 'Donation';
		case Api.AutomationTriggerType.Texting:
			return 'TextingCampaign';
		case Api.AutomationTriggerType.Meeting:
			return 'MeetingReminder';
		case Api.AutomationTriggerType.EventRegistration:
			return 'EventRegistration';
		case Api.AutomationTriggerType.NewMatterTrigger:
			return 'NewMatter';
		case Api.AutomationTriggerType.MatterStageChangedTrigger:
			return 'MatterStageChanged';
		case Api.AutomationTriggerType.MatterStatusChangedTrigger:
			return 'MatterStatusChanged';
		default:
			return null;
	}
}

export function useAutomationTemplateAddTriggerMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate, variables: MutationVariables) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ id, trigger, impersonationContext }: MutationVariables) => {
			const triggerPath = getTriggerPath(trigger);
			if (!triggerPath) {
				return Promise.reject({ success: false, systemMessage: 'Unknown trigger type' });
			}
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}/Trigger/${triggerPath}`,
					impersonationContext,
				}),
				'PUT',
				trigger
			);
		},
		onError,
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(variables.id) });
			invalidateAllAvailableAutomationTriggersQueries();
			onSuccess?.(data, variables);
		},
	});
}
